import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit3,
  Building2,
  Phone,
  Mail,
  MapPin,
  FileText,
  User,
  Calendar,
  Activity,
  Loader2,
  Briefcase,
  IndianRupee,
  ShieldCheck,
  Check
} from 'lucide-react';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import styles from './ViewVendor.module.css';
import api from '../../api/axios';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../components/Common/Footer/Footer';
import toast from 'react-hot-toast';
import NotFound from '../Error/NotFound';

import VendorDrawer from '../../components/Vendors/VendorDrawer';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const ViewVendor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [vendor, setVendor] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchVendor = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/vendors/${id}`);
      if (response.data.success) {
        setVendor(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching vendor details:', error);
      toast.error('Failed to load vendor details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendor();
  }, [id]);

  const getInitials = (name) => {
    if (!name) return 'V';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const getAvatarColor = (name) => {
    const colors = [
      '#f97316', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', 
      '#ec4899', '#f59e0b', '#6366f1', '#14b8a6', '#f43f5e'
    ];
    let hash = 0;
    const str = name || 'Vendor';
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };


  if (loading) {
    return (
      <div className={`${styles['view-vendor-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={40} className={styles['spinner']} />
            <p>Fetching Vendor Profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!vendor) {
    return <NotFound />;
  }


  return (
    <div className={`${styles['view-vendor-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          {/* Page Header */}
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Vendor Details</h1>
              <p>View comprehensive profile, contact details, and account status</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => navigate('/vendors')}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => setIsDrawerOpen(true)}>
                <Edit3 size={18} />
                <span>Edit Profile</span>
              </button>
            </div>
          </div>

          {/* Dashboard Header Card */}
          <div className={styles['vendor-dashboard-header']}>
            <div className={styles['header-main']}>
              <div 
                className={styles['avatar-large']}
                style={{ backgroundColor: getAvatarColor(vendor.vendor_name) }}
              >
                {getInitials(vendor.vendor_name)}
              </div>
              <div className={styles['vendor-basic-info']}>
                <div className={styles['title-flex']}>
                  <h1>{vendor.vendor_name}</h1>
                  <span className={`${styles['status-badge']} ${styles['verified']}`}>
                    <ShieldCheck size={14} /> VERIFIED VENDOR
                  </span>
                </div>
                <div className={styles['vendor-meta']}>
                  <div className={styles['meta-item']}>
                    <User size={16} />
                    <span>Contact Person: <strong>{vendor.contact_person_name || 'Not Assigned'}</strong></span>
                  </div>
                  <div className={styles['meta-item']}>
                    <Calendar size={16} />
                    <span>Member Since: <strong>{new Date(vendor.created_at).getFullYear()}</strong></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Row */}
          <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-orange']}`}><Briefcase size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Active POs</label>
                <h2>04</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-green']}`}><IndianRupee size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Total Business</label>
                <h2>₹24.8L</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-blue']}`}><ShieldCheck size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Trust Score</label>
                <h2 style={{ color: '#10b981' }}>98/100</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-slate']}`}><Activity size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Last Activity</label>
                <h2>Oct 24, 2023</h2>
              </div>
            </div>
          </div>

          <div className={styles['dashboard-grid']}>
            <div className={styles['left-column']}>
              {/* General Information */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Building2 size={20} /></div>
                  <h3>General Information</h3>
                </div>
                <div className={styles['details-list']}>
                  <div className={styles['detail-row']}>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>GST Number</span>
                      <span className={styles['d-value']}>{vendor.gst_number || 'N/A'}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Registered Code</span>
                      <span className={styles['d-value']}>VND-{vendor.id}</span>
                    </div>
                  </div>
                  <div className={styles['detail-item']}>
                    <span className={styles['d-label']}>Official Address</span>
                    <div className={styles['address-box']}>
                      <MapPin size={16} style={{ marginTop: '2px' }} />
                      <span>{vendor.address || 'No physical address registered for this vendor.'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Vendor Activity Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Activity size={18} /></div>
                  <h3>Recent Activity history</h3>
                </div>
                <div className={styles['empty-activity']}>
                    <Activity size={32} />
                    <p>Financial transaction and delivery history will be summarized here once integrated.</p>
                </div>
              </div>
            </div>

            <div className={styles['right-column']}>
              {/* Communication Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Phone size={20} /></div>
                  <h3>Communication</h3>
                </div>
                <div className={styles['contact-list']}>
                  <div className={styles['contact-pill']}>
                    <div className={styles['p-icon']}><User size={16} /></div>
                    <div className={styles['p-info']}>
                       <label>CONTACT PERSON</label>
                       <span>{vendor.contact_person_name || 'N/A'}</span>
                    </div>
                  </div>
                  <div className={styles['contact-pill']}>
                    <div className={styles['p-icon']}><Phone size={16} /></div>
                    <div className={styles['p-info']}>
                       <label>PHONE NUMBER</label>
                       <span>{vendor.phone}</span>
                    </div>
                  </div>
                  <div className={styles['contact-pill']}>
                    <div className={styles['p-icon']}><Mail size={16} /></div>
                    <div className={styles['p-info']}>
                       <label>EMAIL ADDRESS</label>
                       <span>{vendor.email || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* metadata Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><FileText size={20} /></div>
                  <h3>Registry Metadata</h3>
                </div>
                <div className={styles['meta-list']}>
                  <div className={styles['meta-row']}>
                    <span className={styles['m-label']}>Registered By</span>
                    <span className={styles['m-value']}>{vendor.creator?.name || 'System Admin'}</span>
                  </div>
                  <div className={styles['meta-row']}>
                    <span className={styles['m-label']}>Creation Date</span>
                    <span className={styles['m-value']}>{new Date(vendor.created_at).toLocaleDateString('en-IN')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </main>
      </div>

      <VendorDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        initialData={vendor}
        onSave={fetchVendor}
      />
    </div>
  );
};

export default ViewVendor;
